import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import FormLayout from '../components/FormLayout';
import FormHeader from '../components/FormHeader';
import Form from '../components/Form';
import Input from '../components/Input';
import Button from '../components/Button';
import Alert from '../components/Alert';
import CustomLink from '../components/CustomLink';

const PasswordResetRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      const sendPasswordReset = httpsCallable(functions, 'sendPasswordReset');
      await sendPasswordReset({ email });
      setStatus('sent');
    } catch (err: unknown) {
      setStatus('error');
      setMessage((err as Error)?.message || 'Failed to send reset email.');
    }
  };

  return (
    <FormLayout>
      <FormHeader
        title="Forgot your password?"
        subtitle="Enter the email you used to sign up. We'll send you a link to reset it securely."
      />

      <Form onSubmit={handleSubmit}>
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          label="Email address"
          disabled={status === 'loading'}
        />

        <Button type="submit" disabled={status === 'loading'}>
          {status === 'loading' ? 'Sending...' : 'Send reset link'}
        </Button>
      </Form>

      {status === 'sent' && (
        <Alert type="success">
          If an account exists for {email}, you will receive a password reset email shortly.
        </Alert>
      )}

      {status === 'error' && <Alert type="error">{message}</Alert>}

      <div className="mt-6 text-center">
        <p className="text-gray-400 text-sm">
          Remember your password? <CustomLink to="/login">Back to login</CustomLink>
        </p>
      </div>
    </FormLayout>
  );
};

export default PasswordResetRequestPage;
